<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import {
  Field,
  Image,
  Popup,
  showFailToast,
  showLoadingToast,
  Swipe,
  SwipeInstance,
  SwipeItem,
} from "vant";

import { useMateTrait } from "@/stores/mateTrait";
import { computed, nextTick, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import InpaintingEditor, {
  InpaintingEditorInstance,
} from "@/components/mobile/generation/inpaint/InpaintingEditor.vue";
import { InpaintType, MatePortrait, Media } from "@/types/entity";
import api from "@/modules/api";
import { usePreviewImg } from "@/use/common";
import { CustomPaintingEditorInstance } from "@/components/mobile/generation/custom-painting/CustomPaintingEditor.vue";
import i18n from "@/locales";

interface InpaintItem {
  media: Media;
  maskBase64?: string;
  promptText?: string;
}

const store = useMateTrait();

// const PromptEditRef = ref<EditPromptsInstance | null>(null);
const BASE64_PNG_PREFIX = "data:image/png;base64,";
const PAINTING_MODE = [
  i18n.global.t("Image.partialRedraw"),
  i18n.global.t("Image.smudgeRetouching"),
];
const VALID_INPAINT_TYPE: { type: InpaintType; name: string }[] = [
  {
    type: 1,
    name: i18n.global.t("Image.redraw.handRepair"),
  },
  {
    type: 2,
    name: i18n.global.t("Image.redraw.addObject"),
  },
  {
    type: 3,
    name: i18n.global.t("Image.redraw.detailRepair"),
  },
  {
    type: 4,
    name: i18n.global.t("Image.redraw.deleteObject"),
  },
  {
    type: 5,
    name: i18n.global.t("Image.redraw.changeClothes"),
  },
  {
    type: 7,
    name: i18n.global.t("Image.redraw.changeExpression"),
  },
];

// State
// const taskStore = useTask();
const route = useRoute();
const router = useRouter();
// 基础数据
const tabIdx = ref<number>(0);
const id = Number(route.query.id);
// const targetTask = computed(() => taskStore.getTask(taskId));
const loading = ref(true);
const inpaintingEditorRef = ref<InpaintingEditorInstance | null>(null);
const customPaintingEditorRef = ref<CustomPaintingEditorInstance | null>(null);
// 模式选择
const type = ref<InpaintType>(1);
// 选区遮罩层
const showMask = ref(true);
const maskBase64 = ref(""); // 实际给服务端的选区（没有透明度）

const currentMedia = ref<Media>();

// 重绘结果缓存
const inpaintList = ref<InpaintItem[]>([]); // 重绘结果
const previewList = computed<InpaintItem[]>(() => {
  const media = currentMedia.value;
  if (!media) return [];
  return [{ media }, ...inpaintList.value];
});
const previewIdx = ref(0);
const selectedPreviewItem = computed(() => previewList.value[previewIdx.value]);
const swipeRef = ref<SwipeInstance | null>(null);
// 提示词
const showPromptPopup = ref(false);
const promptInputText = ref("");
const confirmedText = ref("");
const activePortrait = ref<MatePortrait>();

// Event Handler
const selectInpaintingRegion = () => {
  inpaintingEditorRef.value?.start(selectedPreviewItem.value.media.url);
};
const startCustomInpainting = () => {
  customPaintingEditorRef.value?.start(
    selectedPreviewItem.value.media.url,
    activePortrait.value?.style
  );
};
const startInpainting = async () => {
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.redrawing"),
  });
  try {
    const { data } = await api.inpaintImage({
      mediaId: selectedPreviewItem.value.media.id,
      maskBase64: maskBase64.value.replace(BASE64_PNG_PREFIX, ""),
      prompt: confirmedText.value,
      type: type.value,
      removeBackground: true,
      strength: inpaintingRange.value / 100,
    });
    inpaintList.value.push({ media: data });
    onPreviewIdxChange(previewList.value.length - 1);
    showMask.value = false;
  } finally {
    loadingToast.close();
  }
};
const onFinishedRegionSelection = (params: {
  base64: string;
  file: File;
  maskPreviewBase64: string;
}) => {
  maskBase64.value = params.base64;
  showMask.value = true;
};
const onFinishedCustomInpainting = (media: Media) => {
  inpaintList.value.push({ media });
  onPreviewIdxChange(previewList.value.length - 1);
  showMask.value = false;
};

const onReplaceMedia = async () => {
  const toast = showLoadingToast({
    message: i18n.global.t("Message.updatingVerticalPainting"),
    duration: 0,
  });
  try {
    let { style, promptJson, mateTraitId, name } = activePortrait.value!;
    const { id: mediaId } = previewList.value[previewIdx.value]!.media;
    promptJson = initPromptJson(promptJson);
    let params = {
      name,
      mediaId,
      style,
      traitId: mateTraitId,
      ...promptJson,
    };
    await store.updatePortrait(activePortrait.value!.id, params);
    await store.getOrFetchPortraitList(mateTraitId);
    router.back();
  } catch (e) {
    showFailToast(i18n.global.t("Message.failedToUpdateVerticalPainting"));
    throw e;
  } finally {
    toast.close();
    showEditPortrait.value = false;
  }
};

const clickPrompt = () => {
  promptInputText.value = confirmedText.value;
  if (type.value === 7) {
    showFacePromptModal.value = true;
  } else {
    showPromptPopup.value = true;
  }
};

const showFacePromptModal = ref(false);
const prompt = ref("");
const clickFacePromptConfirm = async () => {
  confirmedText.value = prompt.value;
  showFacePromptModal.value = false;
};

const clickSavePortrait = () => {
  showEditPortrait.value = true;
};

function onPreviewIdxChange(idx: number) {
  previewIdx.value = idx;
  swipeRef.value?.swipeTo(previewIdx.value, { immediate: true });
}

const showEditPortrait = ref(false);
const activeName = ref("");

const onClickSaveName = async () => {
  const toast = showLoadingToast({
    message: i18n.global.t("Message.creatingVerticalPainting"),
    duration: 0,
  });
  try {
    let { style, promptJson, mateTraitId } = activePortrait.value!;
    const { id: mediaId } = previewList.value[previewIdx.value]!.media;
    promptJson = initPromptJson(promptJson);
    let params = {
      name: activeName.value,
      traitId: mateTraitId,
      mediaId,
      style,
      ...promptJson,
    };
    await store.createPortrait(params);
    await store.getOrFetchPortraitList(Number(mateTraitId));
    router.back();
  } catch (e) {
    showFailToast(i18n.global.t("Message.failedToUpdateVerticalPainting"));
    throw e;
  } finally {
    toast.close();
    showEditPortrait.value = false;
  }
};

const initPromptJson = (promptJson: any) => {
  if (!promptJson) promptJson = {};
  if (!promptJson.clothing)
    promptJson.clothing = {
      prompt: "",
      negativePrompt: "",
    };
  if (!promptJson.pose)
    promptJson.pose = {
      prompt: "",
      negativePrompt: "",
    };
  if (!promptJson.faceExpression)
    promptJson.faceExpression = {
      prompt: "",
      negativePrompt: "",
    };
  return promptJson;
};

const customStrengthMap = {
  1: 75,
  3: 75,
  7: 25,
};

const clickSwitchMap = (_type: InpaintType) => {
  type.value = _type;
  inpaintingRange.value = customStrengthMap[_type as 1 | 3 | 7] || 75;
};

const onStrengthUpdate = (value: number) => {
  customStrengthMap[type.value as 1 | 3 | 7] = value;
};

// Lifecycle
onMounted(async () => {
  loading.value = true;
  try {
    const res = await api.readMateTraitPortraitPage({ filter: { id } });
    activePortrait.value = res.page.data[0];
    currentMedia.value = res.page.data[0].media;
  } finally {
    loading.value = false;
    nextTick(() => {
      swipeRef.value?.resize();
    });
  }
});

const inpaintingRange = ref(75);
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto] overflow-hidden">
    <threads-nav
      :show-center="false"
      back-double-check
      :back-double-check-message="$t('Message.exitRedrawnImage')"
      show-back
    >
      <template #right>
        <button class="btn btn-sm" @click="clickSavePortrait">
          {{ $t("Buttons.saveNewPortrait") }}
        </button>
        <button
          :disabled="inpaintList.length === 0"
          class="btn btn-sm btn-primary"
          @click="onReplaceMedia"
        >
          {{ $t("Buttons.coverOriginalImage") }}
        </button>
      </template>
    </threads-nav>
    <div v-if="loading" class="flex justify-center items-center">
      <div class="loading"></div>
    </div>
    <div
      v-else
      class="h-full grid grid-rows-[1fr_auto_auto] place-items-center overflow-hidden"
    >
      <!--  图片 + 提示词 + 预览列表  -->
      <div class="w-full h-full overflow-hidden grid grid-rows-[1fr_auto_auto]">
        <div class="w-full h-full relative overflow-hidden">
          <swipe
            ref="swipeRef"
            :loop="false"
            class="w-full h-full bg-base-200"
            indicator-color="white"
            @change="
              (idx) => {
                previewIdx = idx;
              }
            "
          >
            <swipe-item
              v-for="item in previewList"
              :key="item.media.id"
              class="relative"
            >
              <Image
                :src="usePreviewImg(item.media.url)"
                class="w-full h-full pointer-events-none"
                fit="scale-down"
                crossorigin="anonymous"
              />
              <Image
                v-show="maskBase64 && showMask && tabIdx === 0"
                :src="maskBase64"
                class="absolute top-0 left-0 right-0 bottom-0 bg-white/20 opacity-40 pointer-events-none"
                fit="scale-down"
                @load="loading = false"
                crossorigin="anonymous"
              />
            </swipe-item>
            <template #indicator="{ active, total }">
              <div
                class="absolute left-2 bottom-2 bg-white/10 p-1 rounded text-xs"
              >
                {{ active + 1 }} / {{ total }}
              </div>
            </template>
          </swipe>
          <div
            v-if="[1, 3, 7].includes(type)"
            class="flex items-center gap-4 flex-col absolute bg-base-100 right-0 top-0 p-4 border border-primary border-t-0"
          >
            <span class="text-xs">{{ $t("Texts.redrawWidth") }}</span>
            <VanSlider
              v-model="inpaintingRange"
              :max="100"
              :min="0"
              :step="5"
              class="w-44"
              size="sm"
              @change="onStrengthUpdate"
            >
              <template #button>
                <button class="btn btn-circle btn-xs">
                  {{ inpaintingRange }}
                </button>
              </template>
            </VanSlider>
          </div>
          <div
            v-if="maskBase64 && tabIdx === 0"
            class="absolute right-2 bottom-2"
          >
            <button class="btn btn-xs" @click="showMask = !showMask">
              <i v-show="!showMask" class="iconfont icon-eye-regular"></i>
              <i v-show="showMask" class="iconfont icon-eye-slash"></i>
            </button>
          </div>
        </div>
        <div
          v-show="confirmedText"
          class="w-full p-2 bg-neutral max-h-20 overflow-y-auto"
        >
          {{ confirmedText }}
        </div>
        <div class="w-full border overflow-x-auto rounded-bl rounded-br">
          <div class="flex h-full">
            <img
              v-for="(item, idx) in previewList"
              :key="item.media.id"
              :class="{
                'border-primary': idx === previewIdx,
                'border-transparent': idx !== previewIdx,
              }"
              :src="usePreviewImg(item.media.url)"
              alt="preview"
              class="h-12 md:h-16 border shrink-0 w-auto min-w-12"
              @click="onPreviewIdxChange(idx)"
              crossorigin="anonymous"
            />
          </div>
        </div>
      </div>
      <div class="flex flex-col w-full py-2">
        <div class="tabs tabs-xs md:tabs-sm tabs-bordered" role="tablist">
          <div
            v-for="(item, idx) in PAINTING_MODE"
            :key="idx"
            :class="{ 'tab-active': tabIdx === idx }"
            class="tab"
            role="tab"
            @click="tabIdx = idx"
          >
            {{ item }}
          </div>
        </div>
        <div
          v-show="tabIdx === 0"
          class="flex flex-col items-center justify-center gap-3 p-4"
        >
          <div class="flex justify-center items-center w-full">
            <div
              class="tabs tabs-xs md:tabs-sm tabs-boxed w-fit"
              role="tablist"
            >
              <div
                v-for="item in VALID_INPAINT_TYPE"
                :key="item.type"
                :class="{ 'tab-active': type === item.type }"
                class="tab"
                role="tab"
                @click="clickSwitchMap(item.type)"
              >
                {{ item.name }}
              </div>
            </div>
          </div>
          <div class="text-base-content/50">
            {{ $t("Texts.redrawBasedTh", { index: previewIdx + 1 }) }}
          </div>
          <div class="flex gap-2">
            <button
              :class="{
                'btn-primary': !maskBase64,
                '': maskBase64,
              }"
              class="btn btn-sm"
              @click="selectInpaintingRegion"
            >
              {{ $t("Buttons.selectRedrawArea") }}
            </button>
            <button v-show="maskBase64" class="btn btn-sm" @click="clickPrompt">
              {{ $t("Buttons.addPrompt") }}
            </button>
            <button
              v-show="maskBase64"
              class="btn btn-sm btn-primary"
              @click="startInpainting"
            >
              {{ $t("Buttons.startRedrawing") }}
            </button>
          </div>
        </div>
        <div
          v-show="tabIdx === 1"
          class="flex flex-col items-center justify-center gap-3 p-4"
        >
          <div class="text-base-content/50">
            {{ $t("Texts.redrawBasedTh", { index: previewIdx + 1 }) }}
          </div>
          <button class="btn btn-sm btn-primary" @click="startCustomInpainting">
            {{ $t("Buttons.enterSmearCanvas") }}
          </button>
        </div>
      </div>
    </div>
    <inpainting-editor
      ref="inpaintingEditorRef"
      @on-finished="onFinishedRegionSelection"
    />
    <GenerationPortraitPngEdit
      ref="customPaintingEditorRef"
      @on-finished="onFinishedCustomInpainting"
    />
    <popup
      v-model:show="showFacePromptModal"
      class="grid grid-rows-[auto_1fr] overflow-hidden h-[90%]"
      position="bottom"
      round
      teleport="body"
    >
      <threads-nav>
        <template #left>
          <div></div>
        </template>
        <template #center>{{ $t("Texts.redrawPromptWord") }}</template>
        <template #right>
          <button
            class="btn btn-primary btn-sm"
            @click="clickFacePromptConfirm"
          >
            {{ $t("Buttons.sure") }}
          </button>
        </template>
      </threads-nav>
      <edit-prompts
        v-model:prompt="prompt"
        mode="face"
        :show-negative="false"
        class="overflow-hidden"
      />
    </popup>

    <popup
      v-model:show="showPromptPopup"
      class="max-h-[50%] grid grid-rows-[auto_1fr] overflow-hidden"
      position="bottom"
      round
      teleport="body"
    >
      <threads-nav>
        <template #left>
          <div></div>
        </template>
        <template #center>{{ $t("Texts.redrawPromptWord") }}</template>
        <template #right>
          <button class="btn btn-ghost btn-sm" @click="promptInputText = ''">
            {{ $t("Buttons.clear") }}
          </button>
          <button
            class="btn btn-primary btn-sm"
            @click="
              confirmedText = promptInputText;
              showPromptPopup = false;
            "
          >
            {{ $t("Buttons.sure") }}
          </button>
        </template>
      </threads-nav>
      <div>
        <field
          v-model="promptInputText"
          autosize
          class="after:hidden overflow-auto"
          :placeholder="$t('Texts.pleaseEnterPromptWord')"
          rows="8"
          type="textarea"
        />
      </div>
    </popup>

    <!-- 修改立绘名称 -->
    <popup
      v-model:show="showEditPortrait"
      class="grid grid-rows-[1fr] py-10 px-4"
      position="bottom"
    >
      <div class="flex flex-col gap-4">
        <div class="text-base text-center font-bold">
          {{ activePortrait?.name }}
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex flex-col gap-2">
            <span>{{ $t("Texts.newVerticalPaintingName") }}</span>
            <input v-model="activeName" class="input input-bordered" />
          </div>
        </div>
      </div>
      <div class="flex flex-col items-center gap-2 mt-4">
        <button
          class="btn btn-sm btn-primary w-[200px]"
          @click="onClickSaveName"
        >
          <i class="iconfont icon-user-edit"></i>
          {{ $t("Buttons.save") }}
        </button>
        <button class="btn btn-sm w-[200px]" @click="showEditPortrait = false">
          {{ $t("Buttons.back") }}
        </button>
      </div>
    </popup>
  </div>
</template>
